"use client";

import type { DateRange } from "react-day-picker";
import type { LessonListing, LessonsResponse } from "@/features/workspaces/types/lessonListings.ts";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "motion/react";
import { Icon } from "@iconify-icon/react";
import { addDays, format, isAfter, parse, subWeeks } from "date-fns";
import i18n from "i18next";
import { useDebouncedCallback } from "use-debounce";
import { useNavigate, useParams } from "react-router-dom";
import { Calendar, ChevronDown, Search, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar as UICalendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { getDateFnsLocale } from "@/i18n/getDateFnsLocale.ts";
import useWorkspaceInformationStore from "@/features/workspaces/store/useWorkspaceInformationStore.tsx";
import { Badge } from "@/components/ui/badge";
import { TUTOR_WORKSPACE_PATHS } from "@/routes/paths.ts";
import { groupLessonsByTimePeriod } from "@/features/workspaces/utils/dateUtils";
import { getLessonGroupTitle } from "@/features/workspaces/utils/uiUtils";
import {
  formatDateParamsForApi,
  formatLessonTime,
  getSelectedStatusLabel,
  statusOptions,
} from "@/features/workspaces/utils/scheduleUtils.tsx";
import { axiosApiInstance } from "@/libs/axios";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
} from "@/components/ui/pagination";
import { getCardStyles, getStatusBadgeText } from "@/features/workspaces/utils/lessonUtils";
import { LessonHost } from "@/features/calendar/types/create-slot.ts";
import AppLogoIcon from "@/components/LogoIcon.tsx";
import GenericLoader from "@/components/GenericLoader";
import { CoachMarker } from "@/components/CoachMarker.tsx";

// Utility function to parse knowledge_cut date without timezone conversion
const parseKnowledgeCutDate = (knowledgeCut: string | null): Date | undefined => {
  if (!knowledgeCut) return undefined;

  // Parse "2025-07-23 12:32:08" format maintaining the same timezone
  return parse(knowledgeCut, "yyyy-MM-dd HH:mm:ss", new Date());
};

export const TutorLessonListings = () => {
  const { t } = useTranslation(["workspaces", "common", "help"]);
  const [dateRange, setDateRange] = useState<DateRange>({
    from: new Date(),
    to: addDays(new Date(), 7),
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const {
    lessonListings,
    lessonPagination,
    setLessonListings,
    setLessonPagination,
    data: workspaceData,
  } = useWorkspaceInformationStore();
  const { workspaceId } = useParams();
  const redirect = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  // Get knowledge cut date limit
  const knowledgeCutDate = parseKnowledgeCutDate(workspaceData?.knowledge_cut ?? null);

  const fetchLessonListings = async (
    dateFrom: string = format(new Date(), "yyyy-MM-dd"),
    dateTo: string = format(addDays(new Date(), 7), "yyyy-MM-dd"),
    status?: string,
    search?: string,
    page = 1,
  ) => {
    if (!workspaceId) return;
    setIsLoading(true);
    try {
      const params: Record<string, string> = {
        date_from: dateFrom,
        date_to: dateTo,
        page: page.toString(),
      };

      if (status) params.status_id = status;
      if (search) params.keyword = search;

      const { data } = await axiosApiInstance.get<LessonsResponse>(
        `workspaces/${workspaceId}/lessons/tutor/listing`,
        {
          params,
        },
      );

      setLessonListings(data.data);
      setLessonPagination(data.meta);
    } catch (error) {
      console.error("Error fetching lesson listings:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const debouncedFetchListings = useDebouncedCallback(fetchLessonListings, 300);

  const debouncedSearch = useDebouncedCallback((query: string) => {
    const params = formatDateParamsForApi(dateRange, statusFilter, query);

    debouncedFetchListings(params.dateFrom, params.dateTo, params.status, params.search);
  }, 500);

  const handleDateRangeChange = (range: DateRange) => {
    if (range.from && range.to) {
      setDateRange(range);
      const params = formatDateParamsForApi(range, statusFilter, searchQuery);

      debouncedFetchListings(params.dateFrom, params.dateTo, params.status, params.search);
    }
  };

  const handleStatusChange = (value: string) => {
    setStatusFilter(value);
    const params = formatDateParamsForApi(dateRange, value, searchQuery);

    debouncedFetchListings(params.dateFrom, params.dateTo, params.status, params.search);
  };

  const handlePageChange = (page: number) => {
    const params = formatDateParamsForApi(dateRange, statusFilter, searchQuery);

    debouncedFetchListings(params.dateFrom, params.dateTo, params.status, params.search, page);
  };

  const clearSearch = () => {
    setSearchQuery("");
    const params = formatDateParamsForApi(dateRange, statusFilter, "");

    debouncedFetchListings(params.dateFrom, params.dateTo, params.status, params.search);
  };

  const resetFilters = () => {
    const today = new Date();
    let fromDate: Date, toDate: Date;

    if (knowledgeCutDate) {
      // If knowledge_cut exists, set range ending at knowledge_cut with 30 days before
      toDate = knowledgeCutDate;
      fromDate = addDays(knowledgeCutDate, -30);
    } else {
      // Default behavior: today to 30 days ahead
      fromDate = today;
      toDate = addDays(today, 30);
    }

    setDateRange({ from: fromDate, to: toDate });
    setStatusFilter("all");
    setSearchQuery("");

    const params = formatDateParamsForApi({ from: fromDate, to: toDate }, "all", "");

    debouncedFetchListings(params.dateFrom, params.dateTo, params.status, params.search);
  };

  useEffect(() => {
    if (workspaceId) {
      const today = new Date();
      let fromDate: Date, toDate: Date;

      if (knowledgeCutDate) {
        // If knowledge_cut exists, set range ending at knowledge_cut with 30 days before
        toDate = knowledgeCutDate;
        fromDate = addDays(knowledgeCutDate, -30);
      } else {
        // Default behavior: today to 30 days ahead
        fromDate = today;
        toDate = addDays(today, 30);
      }

      setDateRange({ from: fromDate, to: toDate });
      const params = formatDateParamsForApi(
        { from: fromDate, to: toDate },
        statusFilter,
        searchQuery,
      );

      debouncedFetchListings(params.dateFrom, params.dateTo, params.status, params.search);
    }
  }, [workspaceId]);

  const handleLessonClick = (lesson: LessonListing) => {
    redirect(`${TUTOR_WORKSPACE_PATHS(workspaceId || "").HOME}/lessons/${lesson.id}/details`);
  };

  const hasActiveFilters = statusFilter !== "all" || searchQuery.trim() !== "";

  const getHostDisplayInfo = (lessonHost?: string) => {
    switch (lessonHost) {
      case LessonHost.Ximly:
        return {
          icon: <AppLogoIcon className="size-3" />,
          name: t("forms:hostMethodSelector.ximly.title", "Ximly"),
        };
      case LessonHost.GoogleMeets:
        return {
          icon: <Icon height="0.75rem" icon="logos:google-meet" width="0.75rem" />,
          name: t("forms:hostMethodSelector.googleMeets.title", "Google Meet"),
        };
      case LessonHost.OtherWay:
        return {
          icon: <Icon height="0.75rem" icon="ph:globe" width="0.75rem" />,
          name: t("forms:hostMethodSelector.otherWay.title", "Other"),
        };
      default:
        return {
          icon: <AppLogoIcon className="size-3" />,
          name: t("forms:hostMethodSelector.ximly.title", "Ximly"),
        };
    }
  };

  return (
    <div className="bg-background min-h-[calc(100dvh_-_4rem)]">
      <ScrollArea className="h-[calc(100dvh_-_4rem)]">
        <div className="mx-auto max-w-6xl p-4">
          {/* Hero Header */}
          <CoachMarker
            description={t("help:coachMarkers.lessonListings.header.description" as any)}
            id="lesson-list-header"
            placement="bottom"
            title={t("help:coachMarkers.lessonListings.header.title" as any)}
          >
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="bg-side-accent/50 text-accent-foreground relative mb-8 overflow-hidden rounded-2xl border-none p-6 pb-4"
              initial={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.4 }}
            >
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-5">
                <div className="absolute -top-20 -right-20 h-40 w-40 rounded-full bg-gradient-to-br from-purple-400 to-pink-600" />
                <div className="absolute -bottom-16 -left-16 h-32 w-32 rounded-full bg-gradient-to-tr from-orange-400 to-red-500" />
              </div>

              <div className="relative">
                <div className="mb-6 flex items-start justify-between">
                  <div className="flex items-center gap-6">
                    <motion.div
                      animate={{ scale: 1 }}
                      className="bg-muted/20 flex h-14 w-14 items-center justify-center rounded-2xl backdrop-blur-sm"
                      initial={{ scale: 0.8 }}
                      transition={{ duration: 0.3, delay: 0.2 }}
                    >
                      <Calendar className="text-accent-foreground h-8 w-8" />
                    </motion.div>
                    <div>
                      <h1 className="text-accent-foreground text-3xl leading-tight font-bold">
                        {t("workspaces:lessons", "Lessons")}
                      </h1>
                      <p className="text-muted-foreground mt-2 text-base">
                        {t("workspaces:manageLessons", "Manage your lessons and schedule")}
                      </p>
                    </div>
                  </div>
                  {hasActiveFilters && (
                    <Button
                      className="bg-muted/30 hover:bg-muted/40 text-accent-foreground border-white/20"
                      size="lg"
                      variant="outline"
                      onClick={resetFilters}
                    >
                      <Icon
                        className="mr-2"
                        height={"1.25rem"}
                        icon="mdi:refresh"
                        width={"1.25rem"}
                      />
                      {t("common:reset", "Reset")}
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          </CoachMarker>

          {/* Enhanced Filter Bar */}
          <CoachMarker
            description={t("help:coachMarkers.lessonListings.filters.description" as any)}
            id="lesson-list-filters"
            placement="bottom"
            title={t("help:coachMarkers.lessonListings.filters.title" as any)}
          >
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="bg-foreground/50 mb-8 rounded-2xl border-none p-4 backdrop-blur-xl"
              initial={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5">
                {/* Search */}
                <div className="relative 2xl:col-span-2">
                  <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                  <Input
                    className="bg-muted/20 border-border/50 hover:bg-muted/30 focus:bg-muted/40 h-10 pr-10 pl-10 text-sm transition-all duration-200"
                    placeholder={t("workspaces:searchLessonsTutor", "Search lessons...")}
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      debouncedSearch(e.target.value);
                    }}
                  />
                  {searchQuery && (
                    <button
                      className="text-muted-foreground hover:text-accent-foreground absolute top-1/2 right-3 -translate-y-1/2 transition-colors"
                      onClick={clearSearch}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>

                {/* From Date */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      className="bg-muted/20 hover:bg-muted/30 border-border/50 h-10 justify-start text-sm transition-all duration-200"
                      variant="outline"
                    >
                      <Calendar className="text-accent-foreground mr-2 h-4 w-4" />
                      <span className="text-sm">
                        {dateRange.from
                          ? format(dateRange.from, "MMM d", {
                              locale: getDateFnsLocale(i18n.language),
                            })
                          : t("common:from", "From")}
                      </span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    className="bg-foreground border-border/50 w-auto p-0 backdrop-blur-xl"
                  >
                    <UICalendar
                      initialFocus
                      defaultMonth={
                        knowledgeCutDate
                          ? knowledgeCutDate < addDays(new Date(), 30)
                            ? new Date()
                            : dateRange.from
                          : dateRange.from
                      }
                      locale={getDateFnsLocale(i18n.language)}
                      mode="single"
                      selected={dateRange.from}
                      toDate={knowledgeCutDate}
                      weekStartsOn={1}
                      onSelect={(date) => {
                        if (date) {
                          const newTo =
                            dateRange.to && isAfter(dateRange.to, date)
                              ? dateRange.to
                              : addDays(date, 7);

                          handleDateRangeChange({ from: date, to: newTo });
                        }
                      }}
                    />
                  </PopoverContent>
                </Popover>

                {/* To Date */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      className="bg-muted/20 hover:bg-muted/30 border-border/50 h-10 justify-start text-sm transition-all duration-200"
                      variant="outline"
                    >
                      <Calendar className="text-accent-foreground mr-2 h-4 w-4" />
                      <span className="text-sm">
                        {dateRange.to
                          ? format(dateRange.to, "MMM d", {
                              locale: getDateFnsLocale(i18n.language),
                            })
                          : t("common:to", "To")}
                      </span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    className="bg-foreground border-border/50 w-auto p-0 backdrop-blur-xl"
                  >
                    <UICalendar
                      initialFocus
                      defaultMonth={
                        knowledgeCutDate
                          ? knowledgeCutDate < addDays(new Date(), 30)
                            ? new Date()
                            : dateRange.to
                          : dateRange.to
                      }
                      locale={getDateFnsLocale(i18n.language)}
                      mode="single"
                      selected={dateRange.to}
                      toDate={knowledgeCutDate}
                      weekStartsOn={1}
                      onSelect={(date) => {
                        if (date) {
                          const newFrom =
                            dateRange.from && isAfter(date, dateRange.from)
                              ? dateRange.from
                              : subWeeks(date, 1);

                          handleDateRangeChange({ from: newFrom, to: date });
                        }
                      }}
                    />
                  </PopoverContent>
                </Popover>

                {/* Status Filter */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      className="bg-muted/20 hover:bg-muted/30 border-border/50 h-10 justify-between text-sm transition-all duration-200"
                      variant="outline"
                    >
                      <span className="truncate">
                        {t(
                          `workspaces:status.${getSelectedStatusLabel(statusFilter)}`,
                          getSelectedStatusLabel(statusFilter),
                        )}
                      </span>
                      <ChevronDown className="text-muted-foreground ml-2 h-4 w-4 shrink-0" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-foreground border-border/50 w-40 backdrop-blur-xl">
                    <DropdownMenuRadioGroup value={statusFilter} onValueChange={handleStatusChange}>
                      {statusOptions.map((option) => (
                        <DropdownMenuRadioItem key={option.id} value={option.id}>
                          {t(`workspaces:status.${option.label}`, option.label)}
                        </DropdownMenuRadioItem>
                      ))}
                    </DropdownMenuRadioGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </motion.div>
          </CoachMarker>

          {/* Content */}
          {renderLessonContent()}
        </div>
      </ScrollArea>
    </div>
  );

  function renderLessonContent() {
    if (isLoading) {
      return (
        <motion.div
          animate={{ opacity: 1, scale: 1 }}
          className="bg-foreground/50 flex h-96 flex-col items-center justify-center rounded-2xl backdrop-blur-xl"
          initial={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.4 }}
        >
          <GenericLoader />
        </motion.div>
      );
    }

    if (!lessonListings || lessonListings.length === 0) {
      return (
        <motion.div
          animate={{ opacity: 1, scale: 1 }}
          className="bg-foreground/50 flex h-96 flex-col items-center justify-center rounded-2xl shadow-2xl backdrop-blur-xl"
          initial={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.4 }}
        >
          <div className="bg-muted/20 mb-6 flex h-20 w-20 items-center justify-center rounded-2xl">
            <Icon
              className="text-muted-foreground"
              height={"2.5rem"}
              icon="mdi:calendar-blank"
              width={"2.5rem"}
            />
          </div>
          <h3 className="text-accent-foreground mb-2 text-xl font-semibold">
            {t("workspaces:noLessonsFound", "No lessons found")}
          </h3>
          <p className="text-muted-foreground max-w-md text-center">
            {t("workspaces:noLessons", "You don't have any lessons for the selected criteria.")}
          </p>
        </motion.div>
      );
    }

    return (
      <div className="space-y-6">
        {Object.entries(groupLessonsByTimePeriod(lessonListings)).map(([groupKey, lessons]) => {
          if (lessons.length === 0) return null;

          return (
            <motion.div
              key={groupKey}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-4"
              initial={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="flex items-center gap-4">
                <h3 className="text-accent-foreground text-xl font-bold">
                  {getLessonGroupTitle(groupKey, t)}
                </h3>
                <div className="bg-border h-px flex-1" />
                <Badge
                  className="bg-foreground/50 text-muted-foreground border-border/50 rounded-full px-3 py-1 font-medium backdrop-blur-xl"
                  variant="outline"
                >
                  {lessons.length}
                </Badge>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {lessons.map((lesson, index) => {
                  const statusColors = getCardStyles(lesson.status.id);
                  const lessonDate = new Date(lesson.start_time);
                  const hostInfo = getHostDisplayInfo(lesson.lesson_host as LessonHost);

                  const lessonCard = (
                    <motion.div
                      key={lesson.id}
                      animate={{ opacity: 1, y: 0 }}
                      initial={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.2, delay: index * 0.05 }}
                    >
                      <div
                        className={`group ${statusColors.card} hover:bg-muted/60 hover:shadow-3xl relative cursor-pointer overflow-hidden rounded-2xl border-none p-4 transition-all duration-300 hover:scale-[1.02] ${statusColors.border} border-l-4 backdrop-blur-xl`}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleLessonClick(lesson)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            handleLessonClick(lesson);
                          }
                        }}
                      >
                        {/* Background Pattern */}
                        <div className="absolute inset-0 opacity-5">
                          <div className="absolute -top-10 -right-10 h-20 w-20 rounded-full bg-gradient-to-br from-purple-400 to-pink-600" />
                        </div>

                        <div className="relative">
                          {/* Status Badge */}
                          <div className="absolute -top-0.5 -right-2 z-10">
                            <Badge
                              className={`rounded-full px-3 py-1 text-xs font-medium shadow-lg ${statusColors.badge}`}
                            >
                              {getStatusBadgeText(lesson.status.id, t, lesson.status.name)}
                            </Badge>
                          </div>

                          {/* Lesson Content */}
                          <div className="mb-3 w-full">
                            <h3
                              className={`mb-2 w-full max-w-[75%] truncate text-base font-semibold max-sm:max-w-[60%] ${statusColors.text}`}
                            >
                              {lesson.topic || t("workspaces:untitledLesson", "Untitled Lesson")}
                            </h3>
                            <p className="text-muted-foreground flex items-center gap-2 text-sm">
                              <Icon
                                className="text-accent-foreground"
                                height={"1rem"}
                                icon={lesson.subject.icon || "mdi:book-outline"}
                                width={"1rem"}
                              />
                              {lesson.subject.name}
                            </p>
                          </div>

                          {/* Date and Time */}
                          <div className="bg-muted/20 mb-3 rounded-xl p-2 backdrop-blur-sm">
                            <div className="mb-2 flex items-center gap-2">
                              <Calendar className="text-muted-foreground h-4 w-4" />
                              <span className="text-sm font-medium">
                                {format(lessonDate, "EEEE, MMM d, yyyy")}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Icon
                                className="text-muted-foreground"
                                height={"1rem"}
                                icon="mdi:clock-outline"
                                width={"1rem"}
                              />
                              <span className="text-sm">
                                {formatLessonTime(lesson.start_time, lesson.end_time)}
                              </span>
                            </div>
                          </div>

                          {/* Platform and Participants */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {hostInfo.icon}
                              <span className="text-muted-foreground text-sm font-medium">
                                {hostInfo.name}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Icon
                                className="text-muted-foreground"
                                height={"1rem"}
                                icon="mdi:account-group"
                                width={"1rem"}
                              />
                              <span className="text-muted-foreground text-sm font-medium">
                                {lesson.participants.length}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );

                  if (index === 0) {
                    return (
                      <CoachMarker
                        key={`coach-marker-${lesson.id}`}
                        description={t("help:coachMarkers.lessonListings.card.description" as any)}
                        id="lesson-list-card-item"
                        placement="bottom"
                        title={t("help:coachMarkers.lessonListings.card.title" as any)}
                      >
                        {lessonCard}
                      </CoachMarker>
                    );
                  }

                  return lessonCard;
                })}
              </div>
            </motion.div>
          );
        })}

        {/* Enhanced Pagination */}
        {lessonPagination && lessonPagination.last_page > 1 && (
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="bg-foreground/50 flex justify-center rounded-2xl p-4 shadow-2xl backdrop-blur-xl"
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.4 }}
          >
            <Pagination>
              <PaginationContent className="gap-1">
                {/* Previous page button */}
                <PaginationItem>
                  <PaginationLink
                    aria-disabled={lessonPagination.current_page === 1}
                    className={
                      lessonPagination.current_page === 1
                        ? "pointer-events-none opacity-50"
                        : "hover:bg-muted/30 transition-colors"
                    }
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (lessonPagination.current_page > 1) {
                        handlePageChange(lessonPagination.current_page - 1);
                      }
                    }}
                  >
                    <Icon height={"1rem"} icon="mdi:chevron-left" width={"1rem"} />
                  </PaginationLink>
                </PaginationItem>

                {/* Generate page numbers */}
                {(() => {
                  const pages = [];
                  const totalPages = lessonPagination.last_page;
                  const currentPage = lessonPagination.current_page;

                  // Always show first page
                  if (currentPage > 3) {
                    pages.push(
                      <PaginationItem key={1}>
                        <PaginationLink
                          className="hover:bg-muted/30 transition-colors"
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            handlePageChange(1);
                          }}
                        >
                          1
                        </PaginationLink>
                      </PaginationItem>,
                    );

                    // Add ellipsis if needed
                    if (currentPage > 4) {
                      pages.push(
                        <PaginationItem key="ellipsis1">
                          <span className="px-2">...</span>
                        </PaginationItem>,
                      );
                    }
                  }

                  // Show pages around current page
                  for (
                    let i = Math.max(1, currentPage - 1);
                    i <= Math.min(totalPages, currentPage + 1);
                    i++
                  ) {
                    pages.push(
                      <PaginationItem key={i}>
                        <PaginationLink
                          className={
                            i === currentPage
                              ? "bg-accent-foreground/90 text-background"
                              : "hover:bg-muted/30 transition-colors"
                          }
                          href="#"
                          isActive={i === currentPage}
                          onClick={(e) => {
                            e.preventDefault();
                            handlePageChange(i);
                          }}
                        >
                          {i}
                        </PaginationLink>
                      </PaginationItem>,
                    );
                  }

                  // Add ellipsis if needed
                  if (currentPage < totalPages - 2) {
                    pages.push(
                      <PaginationItem key="ellipsis2">
                        <span className="px-2">...</span>
                      </PaginationItem>,
                    );
                  }

                  // Always show last page if not already included
                  if (currentPage < totalPages - 1) {
                    pages.push(
                      <PaginationItem key={totalPages}>
                        <PaginationLink
                          className="hover:bg-muted/30 transition-colors"
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            handlePageChange(totalPages);
                          }}
                        >
                          {totalPages}
                        </PaginationLink>
                      </PaginationItem>,
                    );
                  }

                  return pages;
                })()}

                {/* Next page button */}
                <PaginationItem>
                  <PaginationLink
                    aria-disabled={lessonPagination.current_page === lessonPagination.last_page}
                    className={
                      lessonPagination.current_page === lessonPagination.last_page
                        ? "pointer-events-none opacity-50"
                        : "hover:bg-muted/30 transition-colors"
                    }
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (lessonPagination.current_page < lessonPagination.last_page) {
                        handlePageChange(lessonPagination.current_page + 1);
                      }
                    }}
                  >
                    <Icon height={"1rem"} icon="mdi:chevron-right" width={"1rem"} />
                  </PaginationLink>
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </motion.div>
        )}
      </div>
    );
  }
};
